import { useEffect, useRef, useState } from "react";
import api from "../lib/typedApi";
import { useAuth } from "../lib/auth";
import { PageHeader, NBCard, NBInput, NBButton, NBSelect } from "../components/UI";
import { toast } from "sonner";
import { Send, Sparkles, Bot, User as UserIcon, RefreshCw, Mail, Clock, Save } from "lucide-react";

export default function AIAssistant() {
  const { user, refresh } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [weeklyReminder, setWeeklyReminder] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [tzOptions, setTzOptions] = useState([]);
  const [settings, setSettings] = useState({
    timezone: "UTC",
    briefing_hour: 8,
    weekly_email_enabled: true,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/api/ai/history?session_id=default");
    setMessages(data);
  };

  useEffect(() => {
    load();
    api.get("/api/family/timezones").then(({ data }) => setTzOptions(data.timezones || []));
  }, []);

  useEffect(() => {
    if (user?.family) {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      setSettings({
        timezone: user.family.timezone || detected,
        briefing_hour: user.family.briefing_hour ?? 8,
        weekly_email_enabled: user.family.weekly_email_enabled !== false,
      });
    }
  }, [user]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || loading) return;
    const userMsg = text;
    setText("");
    setLoading(true);
    setMessages((m) => [...m, { id: "tmp-u", role: "user", content: userMsg }, { id: "tmp-a", role: "assistant", content: "..." }]);
    try {
      await api.post("/api/ai/chat", { message: userMsg, session_id: "default" });
      load();
    } catch (err) {
      toast.error("AI service error");
      load();
    }
    setLoading(false);
  };

  const generateWeekly = async () => {
    setReminderLoading(true);
    try {
      const { data } = await api.post("/api/ai/weekly-reminder");
      setWeeklyReminder(data.reminder);
      toast.success("Weekly reminder generated!");
    } catch {
      toast.error("Failed to generate");
    }
    setReminderLoading(false);
  };

  const emailToFamily = async () => {
    setEmailLoading(true);
    try {
      const { data } = await api.post("/api/ai/send-weekly-email", { app_url: window.location.origin });
      if (data.sent > 0) {
        toast.success(`Briefing emailed to ${data.sent} member${data.sent > 1 ? "s" : ""}!`);
      } else {
        toast.error(data.skipped || data.error || "No recipients with email accounts");
      }
    } catch {
      toast.error("Failed to send");
    }
    setEmailLoading(false);
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.patch("/api/family/settings", settings);
      toast.success("Briefing settings saved!");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSavingSettings(false);
  };

  const formatHour = (h) => {
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:00 ${h < 12 ? "AM" : "PM"}`;
  };

  const sendTestEmail = async () => {
    setTestLoading(true);
    try {
      const { data } = await api.post("/api/ai/send-test-email", { app_url: window.location.origin });
      toast.success(`Test briefing sent to ${data.to}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test");
    }
    setTestLoading(false);
  };

  const suggestions = [
    "Plan our week",
    "Suggest dinner ideas for the week",
    "What's happening this weekend?",
    "Help me organize chores fairly",
  ];

  return (
    <div data-testid="ai-page">
      <PageHeader
        title="FamilyOS AI"
        subtitle={`Your AI assistant powered by GPT — planning, reminders, and ideas. ${settings.weekly_email_enabled ? `Auto-emails Sundays at ${formatHour(settings.briefing_hour)} ${settings.timezone}.` : "Auto-emails disabled."}`}
        color="#E8B96B"
        action={
          <div className="flex gap-2 flex-wrap">
            <NBButton testId="briefing-settings-btn" color="white" onClick={() => setShowSettings(!showSettings)}>
              <Clock strokeWidth={3} className="w-4 h-4 inline mr-1" />
              Schedule
            </NBButton>
            <NBButton testId="weekly-reminder-btn" color="#B8CFA9" onClick={generateWeekly} disabled={reminderLoading}>
              <RefreshCw strokeWidth={3} className={`w-4 h-4 inline mr-1 ${reminderLoading ? "animate-spin" : ""}`} />
              Preview
            </NBButton>
            <NBButton testId="email-weekly-btn" color="#E9B5A0" onClick={emailToFamily} disabled={emailLoading}>
              <Mail strokeWidth={3} className={`w-4 h-4 inline mr-1 ${emailLoading ? "animate-pulse" : ""}`} />
              Email Family Now
            </NBButton>
          </div>
        }
      />

      {showSettings && (
        <NBCard color="#A8BFD1" className="mb-6" hover={false} data-testid="briefing-settings-card">
          <div className="flex items-center gap-2 mb-4">
            <Clock strokeWidth={3} />
            <h3 className="text-xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Weekly Briefing Schedule</h3>
          </div>
          <form onSubmit={saveSettings} className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-black uppercase tracking-wider mb-1 block">Timezone</label>
              <NBSelect testId="settings-timezone" value={settings.timezone} onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}>
                {tzOptions.includes(settings.timezone) ? null : <option value={settings.timezone}>{settings.timezone}</option>}
                {tzOptions.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </NBSelect>
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider mb-1 block">Sunday Time</label>
              <NBSelect testId="settings-hour" value={settings.briefing_hour} onChange={(e) => setSettings({ ...settings, briefing_hour: parseInt(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{formatHour(h)}</option>)}
              </NBSelect>
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider mb-1 block">Auto-Email</label>
              <div className="flex gap-2">
                {[true, false].map((v) => (
                  <button
                    type="button"
                    key={String(v)}
                    onClick={() => setSettings({ ...settings, weekly_email_enabled: v })}
                    data-testid={`settings-enabled-${v}`}
                    className={`nb-btn flex-1 px-3 py-2 text-sm ${settings.weekly_email_enabled === v ? (v ? "bg-[#B8CFA9]" : "bg-[#E9B5A0]") : "bg-white"}`}
                  >
                    {v ? "Enabled" : "Disabled"}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-3 flex flex-wrap gap-2 justify-end">
              <NBButton testId="send-test-email" type="button" color="#E8B96B" onClick={sendTestEmail} disabled={testLoading}>
                <Mail strokeWidth={3} className={`w-4 h-4 inline mr-1 ${testLoading ? "animate-pulse" : ""}`} />
                {testLoading ? "Sending..." : "Send Test to Me"}
              </NBButton>
              <NBButton testId="save-settings" type="submit" disabled={savingSettings}>
                <Save strokeWidth={3} className="w-4 h-4 inline mr-1" />
                {savingSettings ? "Saving..." : "Save Schedule"}
              </NBButton>
            </div>
          </form>
        </NBCard>
      )}

      {weeklyReminder && (
        <NBCard color="#B8CFA9" className="mb-6" data-testid="weekly-reminder-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles strokeWidth={3} className="w-5 h-5" />
            <h3 className="text-xl font-black" style={{ fontFamily: "var(--hh-display)" }}>This Week's Family Update</h3>
          </div>
          <div className="whitespace-pre-wrap text-sm">{weeklyReminder}</div>
        </NBCard>
      )}

      <div className="nb-card flex flex-col overflow-hidden" style={{ height: "60vh" }}>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-xl border-2 border-black bg-[#E8B96B] flex items-center justify-center mb-4 nb-shadow">
                <Sparkles strokeWidth={3} className="w-8 h-8" />
              </div>
              <p className="font-black text-lg mb-2" style={{ fontFamily: "var(--hh-display)" }}>How can I help your family today?</p>
              <p className="text-sm text-neutral-600 mb-4">I know your members, tasks, events, and shopping list.</p>
              <div className="flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => setText(s)} data-testid={`suggestion-${s.split(" ")[0]}`} className="nb-btn bg-white px-3 py-1.5 text-xs">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role !== "user" && (
                <div className="w-8 h-8 rounded-full border-2 border-black bg-[#E8B96B] flex items-center justify-center shrink-0">
                  <Bot strokeWidth={2.5} className="w-4 h-4" />
                </div>
              )}
              <div className={`max-w-[80%] border-2 border-black rounded-xl px-4 py-2 nb-shadow-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#A8BFD1]" : "bg-white"}`}>
                <p className="text-sm">{m.content}</p>
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 rounded-full border-2 border-black bg-[#A8BFD1] flex items-center justify-center shrink-0">
                  <UserIcon strokeWidth={2.5} className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="border-t-2 border-black p-3 flex gap-2 bg-[#F6EFE4]">
          <NBInput testId="ai-input" placeholder="Ask anything..." value={text} onChange={(e) => setText(e.target.value)} disabled={loading} />
          <NBButton testId="ai-send" type="submit" disabled={loading} color="#E8B96B">
            <Send strokeWidth={3} className="w-4 h-4" />
          </NBButton>
        </form>
      </div>
    </div>
  );
}
